import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, PaymentForm } from 'react-square-web-payments-sdk';
import { useCart } from '../context/CartContext';
import Navigation from '../components/Navigation';

// NOTA: En una app real, estos IDs vendrían de env variables
const APPLICATION_ID = "sandbox-sq0idb-YOUR_SANDBOX_APP_ID"; // Placeholder para el Sandbox de Square
const LOCATION_ID = "main";

export default function Checkout() {
    const { items, totalPrice } = useCart();
    const [step, setStep] = useState<'shipping' | 'payment'>('shipping');
    const [formData, setFormData] = useState({
        email: '',
        firstName: '',
        lastName: '',
        address: '',
        city: '',
        postalCode: '',
        country: 'Colombia',
    });

    if (items.length === 0) {
        return (
            <div className="min-h-screen bg-[var(--vyera-cream)]">
                <Navigation />
                <main className="pt-32 px-[6vw] text-center">
                    <h1 className="vyera-heading-md mb-6">Your bag is empty.</h1>
                    <Link to="/shop" className="vyera-cta">Continue shopping</Link>
                </main>
            </div>
        );
    }

    const handleShippingSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setStep('payment');
    };

    return (
        <div className="min-h-screen bg-[var(--vyera-cream)]">
            <Navigation />
            <main className="pt-24 pb-20 px-[6vw] max-w-[1200px] mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                    {/* Form Side */}
                    <section className="order-2 lg:order-1">
                        <h1 className="vyera-heading-md mb-10 text-[var(--vyera-text-primary)]">
                            {step === 'shipping' ? 'Shipping Information' : 'Payment Details'}
                        </h1>

                        {step === 'shipping' ? (
                            <form onSubmit={handleShippingSubmit} className="space-y-6">
                                <div className="space-y-1">
                                    <label htmlFor="email" className="vyera-label text-[10px]">Contact Email</label>
                                    <input
                                        required
                                        type="email"
                                        id="email"
                                        placeholder="email@example.com"
                                        className="w-full bg-transparent border-b border-[var(--vyera-warm-grey)]/40 py-3 focus:outline-none focus:border-[var(--vyera-burgundy)] transition-colors"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4 pt-4">
                                    <div className="space-y-1">
                                        <label htmlFor="firstName" className="vyera-label text-[10px]">First Name</label>
                                        <input
                                            required
                                            type="text"
                                            id="firstName"
                                            className="w-full bg-transparent border-b border-[var(--vyera-warm-grey)]/40 py-3 focus:outline-none focus:border-[var(--vyera-burgundy)] transition-colors"
                                            value={formData.firstName}
                                            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label htmlFor="lastName" className="vyera-label text-[10px]">Last Name</label>
                                        <input
                                            required
                                            type="text"
                                            id="lastName"
                                            className="w-full bg-transparent border-b border-[var(--vyera-warm-grey)]/40 py-3 focus:outline-none focus:border-[var(--vyera-burgundy)] transition-colors"
                                            value={formData.lastName}
                                            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-1 pt-4">
                                    <label htmlFor="address" className="vyera-label text-[10px]">Shipping Address</label>
                                    <input
                                        required
                                        type="text"
                                        id="address"
                                        className="w-full bg-transparent border-b border-[var(--vyera-warm-grey)]/40 py-3 focus:outline-none focus:border-[var(--vyera-burgundy)] transition-colors"
                                        value={formData.address}
                                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4 pt-4">
                                    <div className="space-y-1">
                                        <label htmlFor="city" className="vyera-label text-[10px]">City</label>
                                        <input
                                            required
                                            type="text"
                                            id="city"
                                            className="w-full bg-transparent border-b border-[var(--vyera-warm-grey)]/40 py-3 focus:outline-none focus:border-[var(--vyera-burgundy)] transition-colors"
                                            value={formData.city}
                                            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label htmlFor="zip" className="vyera-label text-[10px]">Postal Code</label>
                                        <input
                                            required
                                            type="text"
                                            id="zip"
                                            className="w-full bg-transparent border-b border-[var(--vyera-warm-grey)]/40 py-3 focus:outline-none focus:border-[var(--vyera-burgundy)] transition-colors"
                                            value={formData.postalCode}
                                            onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    className="w-full py-5 bg-[var(--vyera-black)] text-[var(--vyera-cream)] font-medium mt-10 hover:opacity-90 transition-opacity uppercase tracking-widest text-xs"
                                >
                                    Continue to Payment
                                </button>
                            </form>
                        ) : (
                            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
                                <p className="vyera-body text-[var(--vyera-text-secondary)] text-sm mb-6">
                                    Secure payment processed by Square. Your data is encrypted.
                                </p>

                                <PaymentForm
                                    applicationId={APPLICATION_ID}
                                    locationId={LOCATION_ID}
                                    cardTokenizeResponseReceived={(token, buyer) => {
                                        console.info({ token, buyer });
                                        alert('Pago procesado correctamente (Modo Simulado). ¡Gracias por tu compra!');
                                    }}
                                >
                                    <CreditCard
                                        buttonProps={{
                                            className: "w-full py-5 bg-[var(--vyera-black)] text-[var(--vyera-cream)] font-medium mt-10 hover:opacity-90 transition-opacity uppercase tracking-widest text-xs",
                                            isLoading: false
                                        }}
                                    >
                                        Pay {totalPrice}
                                    </CreditCard>
                                </PaymentForm>

                                <button
                                    type="button"
                                    onClick={() => setStep('shipping')}
                                    className="w-full text-center text-[10px] uppercase tracking-widest text-[var(--vyera-text-secondary)] hover:text-[var(--vyera-text-primary)] transition-colors"
                                >
                                    Back to Shipping
                                </button>
                            </div>
                        )}
                    </section>

                    {/* Summary Side */}
                    <section className="order-1 lg:order-2 bg-[var(--vyera-black)]/5 p-8 lg:p-12 lg:sticky lg:top-32 h-max border border-[var(--vyera-warm-grey)]/10">
                        <h2 className="vyera-label mb-8 text-[12px] text-[var(--vyera-text-primary)]">Order Summary</h2>
                        <ul className="space-y-6 mb-10">
                            {items.map((item) => (
                                <li key={item.id} className="flex justify-between items-start gap-4">
                                    <div className="flex gap-4">
                                        <div className="w-16 h-20 bg-[var(--vyera-cream)] border border-[var(--vyera-warm-grey)]/20 shrink-0 overflow-hidden">
                                            <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-medium text-[var(--vyera-text-primary)]">{item.name}</h4>
                                            <p className="text-[10px] text-[var(--vyera-text-secondary)] uppercase tracking-tight">
                                                Qty: {item.quantity} {item.size ? `| Size: ${item.size}` : ''}
                                            </p>
                                        </div>
                                    </div>
                                    <span className="text-sm text-[var(--vyera-text-primary)]">{item.price}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="border-t border-[var(--vyera-warm-grey)]/20 pt-6 space-y-4">
                            <div className="flex justify-between text-sm text-[var(--vyera-text-secondary)]">
                                <span>Shipping</span>
                                <span>Calculated at next step</span>
                            </div>
                            <div className="flex justify-between text-lg font-medium text-[var(--vyera-text-primary)] pt-2">
                                <span>Total</span>
                                <span>{totalPrice}</span>
                            </div>
                        </div>
                        <Link to="/shop" className="block text-center mt-8 text-xs text-[var(--vyera-text-secondary)] hover:text-[var(--vyera-burgundy)] underline underline-offset-4">
                            Return to shop
                        </Link>
                    </section>
                </div>
            </main>
        </div>
    );
}
